import { Button, Typography } from '@material-tailwind/react';
import { useEffect, useMemo, useState } from 'react';
import {
  FreeTrialContextItem,
  RecitationResultStatusEnum,
} from '../../constants/types/free-trial/type';
import {
  CookieKey,
  CookieTypeJebsAiRecitation,
  createCookie,
} from '../../utils/cookie';
import { resolveRecitationUrl } from '../../utils/popupWindow';
import { useAppDispatch } from '../../store/hook';
import {
  getFreeTrialRecitationResult,
  reportFreeTrialRecitationPractice,
  reportFreeTrialStepProgress,
} from '../../api/utils/free-trial/api';
import { openAlert } from '../../store/features/modalSlice';
import { getErrorMessages } from '../../api/utils/util-func';
import FreeTrialAttentionEffect from './FreeTrialAttentionEffect';

interface FreeTrialRecitationStepProps {
  freeTrialContext: FreeTrialContextItem;
  onComplete: () => void;
}

type RecitationMode = 'PRACTICE' | 'TEST';

const {
  setCookie: setJebsAiRecitationCookie,
  removeCookie: removeJebsAiRecitationCookie,
} = createCookie<CookieTypeJebsAiRecitation>(CookieKey.JEBS_AI_RECITATION);

const FreeTrialRecitationStep = ({
  freeTrialContext,
  onComplete,
}: FreeTrialRecitationStepProps) => {
  const dispatch = useAppDispatch();

  const [recitationMode, setRecitationMode] = useState<RecitationMode | null>(
    null,
  );
  const [isCookieReady, setIsCookieReady] = useState(false);
  const [isReporting, setIsReporting] = useState(false);

  const isAttemptExhausted = freeTrialContext.remainingAttempts === 0;

  const linkToken = sessionStorage.getItem('freeTrialLinkToken');

  useEffect(() => {
    if (!linkToken) return;

    const reportRecitationProgress = async () => {
      try {
        await reportFreeTrialStepProgress({
          linkToken,
          step: 'recitation',
        });
      } catch (error) {
        console.error('AI 인증제 진행 상태 저장 실패', error);
      }
    };

    void reportRecitationProgress();
  }, [linkToken]);

  const env = process.env.REACT_APP_JEBS_APP_URL || '';
  const isDev = env.includes('dev') || env.includes('local');

  const recitationUrl = useMemo(() => {
    if (!recitationMode) return '';

    const isTest = recitationMode === 'TEST';

    return resolveRecitationUrl(
      `${
        isDev ? 'https://dev-recitation.jebs.kr' : 'https://recitation.jebs.kr'
      }/jebs-recitation-app/recitation/${
        freeTrialContext.recitation.paperId
      }?isTest=${isTest}`,
    );
  }, [freeTrialContext.recitation.paperId, recitationMode, isDev]);

  useEffect(() => {
    if (!recitationUrl) {
      setIsCookieReady(false);
    }

    setIsCookieReady(true);
  }, [recitationUrl]);

  const prepareRecitationCookie = () => {
    setIsCookieReady(false);

    try {
      removeJebsAiRecitationCookie();
    } catch {
      // 기존 쿠키가 없는 경우 무시
    }

    setJebsAiRecitationCookie(
      {
        student: {
          id: freeTrialContext.recitation.studentId,
          uid: '',
        },
        teacher: {
          id: '',
          uid: '',
          classId: '',
        },
        todo: {
          id: freeTrialContext.recitation.todoId,
          type: 'JEBS_AI_RECITATION',
        },
        accessToken: freeTrialContext.recitation.accessToken || '',
      },
      1 / 24,
    );

    setIsCookieReady(true);
  };

  const handleOpenRecitationPractice = async () => {
    try {
      await reportFreeTrialRecitationPractice(linkToken);

      prepareRecitationCookie();
      setRecitationMode('PRACTICE');
    } catch (error) {
      dispatch(
        openAlert({
          message: getErrorMessages(error),
        }),
      );
    }
  };

  const handleOpenRecitationTest = async () => {
    if (!linkToken) return;

    try {
      prepareRecitationCookie();
      setRecitationMode('TEST');
    } catch (error) {
      dispatch(
        openAlert({
          message: getErrorMessages(error),
        }),
      );
    }
  };

  const handleCompleteRecitation = async () => {
    if (!linkToken || isReporting) return;

    try {
      setIsReporting(true);

      const data = await getFreeTrialRecitationResult(linkToken);

      if (data.remainingAttempts === 2) {
        dispatch(
          openAlert({
            message:
              'AI 인증이 완료되지 않았습니다. 완료 후 다시 시도해주세요.',
          }),
        );
        return;
      }

      if (
        data.status === RecitationResultStatusEnum.FAILED &&
        data.remainingAttempts === 1
      ) {
        dispatch(
          openAlert({
            message:
              '아쉽게도 이번 인증은 통과하지 못했어요. 응시 기회가 1회 남아 있습니다.',
          }),
        );
        return;
      }

      onComplete();
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsReporting(false);
    }
  };

  return (
    <section className="mt-5 p-0 md:rounded-2xl md:bg-white md:px-6 md:py-7 md:shadow-[0_6px_20px_rgba(40,25,100,0.05)]">
      {/* 제목 */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
            STEP 3 · 말로 인증해요
          </Typography>

          <Typography className="mt-1 text-[22px] font-black leading-[1.3] text-gray-900 md:text-[26px]">
            AI 스피킹 인증제 도전!
          </Typography>
        </div>

        <span className="w-fit shrink-0 rounded-full bg-[#F4F0FF] px-3 py-1.5 text-[10px] font-bold text-[#6737F4] md:text-[11px]">
          체험판 · 암송 1레슨
        </span>
      </div>

      {!recitationMode ? (
        /* 인증제 진입 전 */
        <div className="mt-3 rounded-2xl border border-[#DED8F2] bg-[#F8F5FF] px-4 py-5 md:mt-6 md:px-8 md:py-10">
          <div className="mx-auto max-w-[520px] text-center">
            <Typography className="text-[18px] font-black text-gray-900 md:text-[20px]">
              {isAttemptExhausted
                ? '인증제 응시 횟수를 모두 사용했어요.'
                : '먼저 연습해 볼까요?'}
            </Typography>

            <Typography className="mt-1.5 text-[12px] leading-5 text-gray-700 md:mt-2 md:text-[13px]">
              {isAttemptExhausted
                ? '무료체험에서 제공되는 인증제 응시 횟수를 모두 사용해 더 이상 연습하거나 응시할 수 없습니다.'
                : '충분히 연습한 뒤 준비가 되면 인증제에 응시해 보세요.'}
            </Typography>

            <div className="mt-4 grid gap-2 sm:grid-cols-2 md:mt-6 md:gap-3">
              <Button
                ripple={false}
                variant="outlined"
                disabled={isAttemptExhausted}
                onClick={handleOpenRecitationPractice}
                className={`h-12 rounded-xl py-0 text-[13px] font-bold normal-case shadow-none transition-colors focus:shadow-none ${
                  isAttemptExhausted
                    ? 'cursor-not-allowed border-gray-200 !bg-gray-100 text-gray-400'
                    : 'border-[#C9BBFF] !bg-white text-[#6737F4] !shadow-[0_2px_6px_rgba(103,55,244,0.08)] hover:!bg-[#F4F0FF] hover:!shadow-[0_2px_6px_rgba(103,55,244,0.12)]'
                }`}
              >
                연습하기
              </Button>

              <Button
                ripple={false}
                disabled={isAttemptExhausted}
                onClick={handleOpenRecitationTest}
                className={`relative h-12 rounded-xl py-0 text-[13px] font-bold normal-case text-white shadow-none transition-all hover:shadow-none focus:shadow-none ${
                  isAttemptExhausted
                    ? 'cursor-not-allowed bg-[#CFC4F5]'
                    : 'bg-[#6737F4] hover:bg-[#5425D6] active:bg-[#4D21C7]'
                }`}
              >
                {!isAttemptExhausted && <FreeTrialAttentionEffect />}

                <span className="relative z-10">응시하기</span>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* AI 인증제 */}
          <div className="mt-3 overflow-hidden rounded-2xl border border-[#DED8F2] bg-[#F8F5FF] md:mt-6">
            {isCookieReady ? (
              <iframe
                src={recitationUrl}
                title={
                  recitationMode === 'PRACTICE'
                    ? 'AI 스피킹 인증제 연습'
                    : 'AI 스피킹 인증제 응시'
                }
                className="h-[calc(100dvh-120px)] min-h-[840px] w-full border-0 md:h-[830px] md:min-h-0"
                allow="microphone; autoplay"
              />
            ) : (
              <div className="flex h-[calc(100dvh-120px)] min-h-[840px] items-center justify-center md:h-[830px] md:min-h-0">
                <Typography className="text-[12px] font-medium text-gray-800">
                  인증제를 준비하고 있어요.
                </Typography>
              </div>
            )}
          </div>

          {/* 하단 액션 */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Typography className="text-[11px] font-medium text-gray-700 md:text-[12px]">
              {recitationMode === 'PRACTICE'
                ? '연습을 마치고 준비가 되면 인증제에 응시해 보세요.'
                : '합격하면 결과 리포트와 우리 아이 암송 유형이 나와요.'}
            </Typography>

            {recitationMode === 'PRACTICE' ? (
              <Button
                ripple={false}
                onClick={handleOpenRecitationTest}
                className="relative h-11 shrink-0 rounded-xl bg-[#6737F4] px-5 py-0 text-[13px] font-bold normal-case text-white shadow-none hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none"
              >
                <FreeTrialAttentionEffect shine={false} />

                <span className="relative z-10">인증제 응시하기</span>
              </Button>
            ) : (
              <Button
                ripple={false}
                onClick={handleCompleteRecitation}
                disabled={isReporting}
                className="relative h-11 shrink-0 rounded-xl bg-[#6737F4] px-5 py-0 text-[13px] font-bold normal-case text-white shadow-none hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none"
              >
                {!isReporting && <FreeTrialAttentionEffect shine={false} />}

                <span className="relative z-10">
                  {isReporting ? '처리 중...' : '인증제 완료! 결과 보기'}
                </span>
              </Button>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default FreeTrialRecitationStep;
